import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const COOKIE = "sme_currency";

export const CURRENCIES = [
  { code: "INR", symbol: "₹", name: "Indian Rupee", locale: "en-IN", country: "IN" },
  { code: "USD", symbol: "$", name: "US Dollar", locale: "en-US", country: "US" },
  { code: "GBP", symbol: "£", name: "Pound Sterling", locale: "en-GB", country: "GB" },
  { code: "AED", symbol: "AED ", name: "UAE Dirham", locale: "en-AE", country: "AE" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar", locale: "en-CA", country: "CA" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", locale: "en-AU", country: "AU" },
];

const FX = { INR: 1, USD: 0.0116, GBP: 0.0091, AED: 0.0426, CAD: 0.0159, AUD: 0.0177 };

const TZ_TO_CURRENCY = {
  "America/New_York": "USD", "America/Chicago": "USD", "America/Los_Angeles": "USD",
  "America/Denver": "USD", "America/Phoenix": "USD", "Europe/London": "GBP",
  "Asia/Dubai": "AED", "America/Toronto": "CAD", "America/Vancouver": "CAD",
  "Australia/Sydney": "AUD", "Australia/Melbourne": "AUD", "Australia/Perth": "AUD",
};

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

function writeCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=31536000;SameSite=Lax`;
}

function detect() {
  const saved = readCookie(COOKIE);
  if (saved && FX[saved]) return saved;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TZ_TO_CURRENCY[tz]) return TZ_TO_CURRENCY[tz];
    if (tz && tz.startsWith("Europe/")) return "GBP";
    if (tz && tz.startsWith("America/")) return "USD";
  } catch (e) {
    /* fall through */
  }
  return "INR";
}

const Ctx = createContext(null);

export function CurrencyProvider({ children }) {
  const [code, setCode] = useState(detect);
  const [autoDetected, setAutoDetected] = useState(!readCookie(COOKIE));

  useEffect(() => {
    writeCookie(COOKIE, code);
  }, [code]);

  const value = useMemo(() => {
    const cur = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];
    const convert = (inr) => inr * FX[cur.code];
    const format = (inr, opts = {}) => {
      const v = convert(inr || 0);
      const decimals = cur.code === "INR" ? 0 : v < 100 ? 2 : 0;
      const n = new Intl.NumberFormat(cur.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(v);
      return `${cur.symbol}${n}${opts.suffix || ""}`;
    };
    return {
      code: cur.code, symbol: cur.symbol, currency: cur, currencies: CURRENCIES,
      setCurrency: (c) => { setCode(c); setAutoDetected(false); },
      convert, format, autoDetected, country: cur.country,
    };
  }, [code, autoDetected]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCurrency = () => useContext(Ctx);

export function formatINR(v) {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v || 0)}`;
}
